import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { cr } from '@/lib/db';
import { useOrgContext } from '@/hooks/useOrgContext';
import { ArrowLeft, User, Scissors, DoorOpen, Clock } from 'lucide-react';
import EvidenceTab from '@/components/evidence/EvidenceTab';
import { EmptyState } from '@/components/EmptyState';
import DocumentationTab from '@/components/documentation/DocumentationTab';
import { OrReadinessBrief } from '@/components/ai/AscAiPanels';
import { ChargeCapturePanel } from '@/components/ChargeCapturePanel';
import { HandoffPanel } from '@/components/handoff/HandoffPanel';

// ============================================================================
// Case Workspace — the per-case operational surface. Persistent case header +
// tabbed content (Revela-style, adapted to surgery ops). Overview and Timeline
// render live case data; Photos/Evidence, Documentation, Charges (draft
// superbill via ChargeCapturePanel), and Handoff (SBAR via cr.case_handoffs)
// are all functional against the private backend. No placeholders remain.
// ============================================================================

interface OrCase {
    case_id: number;
    org_id: string;
    case_number: string | null;
    patient_name: string | null;
    patient_mrn: string | null;
    patient_id: number | null;
    encounter_id: number | null;
    procedure_name: string | null;
    procedure_cpt_code: string | null;
    surgeon_name: string | null;
    anesthesiologist_name: string | null;
    anesthesia_type: string | null;
    or_room: string | null;
    case_status: string | null;
    schedule_status: string | null;
    stage: string | null;
    scheduled_start: string | null;
    actual_start: string | null;
    actual_end: string | null;
    patient_position: string | null;
}

interface CaseEvent {
    event_description: string | null;
    event_type: string | null;
    event_timestamp: string | null;
}

type Tab = 'overview' | 'timeline' | 'documentation' | 'evidence' | 'charges' | 'handoff';
const TABS: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'documentation', label: 'Documentation' },
    { id: 'evidence', label: 'Photos & Evidence' },
    { id: 'charges', label: 'Charges' },
    { id: 'handoff', label: 'Handoff' },
];

function fmt(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function CaseWorkspaceRoute() {
    const { caseId: caseIdParam } = useParams();
    const navigate = useNavigate();
    const { org_id } = useOrgContext();
    const caseId = Number(caseIdParam);
    const [searchParams] = useSearchParams();
    const requestedTab = searchParams.get('tab') as Tab | null;
    const [tab, setTab] = useState<Tab>(
        requestedTab && TABS.some((t) => t.id === requestedTab) ? requestedTab : 'overview'
    );

    const caseQuery = useQuery({
        queryKey: ['case-workspace', caseId],
        enabled: Number.isFinite(caseId),
        queryFn: async (): Promise<OrCase> => {
            const { data, error } = await cr().from('or_cases').select('*').eq('case_id', caseId).single();
            if (error) throw error;
            return data as OrCase;
        },
    });

    const eventsQuery = useQuery({
        queryKey: ['case-workspace-events', caseId],
        enabled: Number.isFinite(caseId) && tab === 'timeline',
        queryFn: async (): Promise<CaseEvent[]> => {
            const { data, error } = await cr().from('case_events').select('event_description, event_type, event_timestamp')
                .eq('case_id', caseId).order('event_timestamp', { ascending: false }).limit(50);
            if (error) throw error;
            return (data ?? []) as CaseEvent[];
        },
    });

    const c = caseQuery.data;

    return (
        <div className="flex flex-col h-full">
            {/* Persistent case header */}
            <div className="bg-pt-panel border-b border-pt-border px-6 py-3">
                <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="flex items-center gap-1.5 text-xs text-pt-text-muted hover:text-pt-text mb-2 [&>svg]:w-3.5 [&>svg]:h-3.5"
                >
                    <ArrowLeft /> Command Center
                </button>
                {caseQuery.isLoading ? (
                    <div className="text-sm text-pt-text-dim"><span className="pt-spinner mr-2" />Loading case…</div>
                ) : caseQuery.isError || !c ? (
                    <div className="text-sm text-pt-red">Case not found or not accessible.</div>
                ) : (
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                        <div className="min-w-0">
                            <div className="text-lg font-semibold truncate">{c.procedure_name ?? 'Procedure'}</div>
                            <div className="text-xs text-pt-text-muted font-mono">
                                {c.case_number ?? `Case #${c.case_id}`}{c.procedure_cpt_code ? ` · CPT ${c.procedure_cpt_code}` : ''}
                            </div>
                        </div>
                        <HeaderChip icon={<User />} label={c.patient_name ?? 'Patient'} sub={c.patient_mrn ? `MRN ${c.patient_mrn}` : undefined} />
                        <HeaderChip icon={<Scissors />} label={c.surgeon_name ?? 'Surgeon'} sub={c.anesthesia_type ?? undefined} />
                        <HeaderChip icon={<DoorOpen />} label={c.or_room ?? 'Room TBD'} sub={c.stage ? c.stage.replace('_', '-') : undefined} />
                        <HeaderChip icon={<Clock />} label={fmt(c.scheduled_start)} sub="scheduled" />
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="bg-pt-panel border-b border-pt-border px-6 flex gap-1 overflow-x-auto">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={[
                            'px-3 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors',
                            tab === t.id ? 'border-pt-cyan text-pt-text' : 'border-transparent text-pt-text-muted hover:text-pt-text',
                        ].join(' ')}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
                {tab === 'overview' && (
                    !c ? <Coming>Case overview unavailable.</Coming> : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl">
                            <Field label="Status" value={c.case_status ?? '—'} />
                            <Field label="Schedule status" value={c.schedule_status ?? '—'} />
                            <Field label="Stage" value={c.stage ? c.stage.replace('_', '-') : '—'} />
                            <Field label="OR room" value={c.or_room ?? '—'} />
                            <Field label="Surgeon" value={c.surgeon_name ?? '—'} />
                            <Field label="Anesthesiologist" value={c.anesthesiologist_name ?? '—'} />
                            <Field label="Anesthesia" value={c.anesthesia_type ?? '—'} />
                            <Field label="Position" value={c.patient_position ?? '—'} />
                            <Field label="Scheduled" value={fmt(c.scheduled_start)} />
                            <Field label="Actual start" value={fmt(c.actual_start)} />
                            <Field label="Actual end" value={fmt(c.actual_end)} />
                            <OrReadinessBrief caseId={c.case_id} />
                        </div>
                    )
                )}

                {tab === 'timeline' && (
                    eventsQuery.isLoading ? <Coming>Loading timeline…</Coming>
                    : (eventsQuery.data ?? []).length === 0 ? <EmptyState title="No case events recorded yet" hint="Milestones appear here as the case moves through pre-op, OR, and recovery." />
                    : (
                        <ol className="relative border-l border-pt-input-border ml-2 space-y-4 max-w-3xl">
                            {(eventsQuery.data ?? []).map((e, i) => (
                                <li key={i} className="ml-4">
                                    <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-pt-cyan border-2 border-pt-panel" />
                                    <div className="text-sm">{e.event_description ?? e.event_type ?? 'Event'}</div>
                                    <div className="text-xs text-pt-text-dim font-mono">{fmt(e.event_timestamp)}</div>
                                </li>
                            ))}
                        </ol>
                    )
                )}

                {tab === 'evidence' && (
                    !c || !org_id ? <Coming>Resolving case context…</Coming>
                    : <EvidenceTab caseId={c.case_id} orgId={org_id} patientId={c.patient_id} encounterId={c.encounter_id} />
                )}

                {tab === 'documentation' && (!c || !org_id ? <Coming>Resolving case context…</Coming> : <DocumentationTab caseId={c.case_id} encounterId={c.encounter_id} orgId={org_id} />)}
                {tab === 'charges' && (!c ? <Coming>Resolving case…</Coming> : <ChargeCapturePanel caseId={c.case_id} encounterId={c.encounter_id} />)}
                {tab === 'handoff' && (!c || !org_id ? <Coming>Resolving case context…</Coming> : <HandoffPanel caseId={c.case_id} orgId={org_id} encounterId={c.encounter_id} currentStage={c.stage} />)}
            </div>
        </div>
    );
}

function HeaderChip({ icon, label, sub }: { icon: React.ReactNode; label: string; sub?: string }) {
    return (
        <div className="flex items-center gap-2 min-w-0 text-pt-text-muted [&>svg]:w-4 [&>svg]:h-4">
            <span className="text-pt-gold shrink-0">{icon}</span>
            <div className="min-w-0">
                <div className="text-sm text-pt-text truncate">{label}</div>
                {sub && <div className="text-[11px] text-pt-text-dim truncate">{sub}</div>}
            </div>
        </div>
    );
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div className="bg-pt-panel border border-pt-border rounded-md px-3 py-2">
            <div className="text-[11px] uppercase tracking-wide text-pt-text-dim">{label}</div>
            <div className="text-sm mt-0.5">{value}</div>
        </div>
    );
}

function Coming({ children }: { children: React.ReactNode }) {
    return <div className="text-sm text-pt-text-dim max-w-2xl">{children}</div>;
}
